/**
 * Amount Discounts Store
 * Manages quantity-based discounts for catalog card display
 * Loads all discounts for current assortment once, then serves from memory
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { getFirebaseDb } from '../../core/firebase/firebase.js'
import { collection, getDocs } from 'firebase/firestore'
import { getDiscountAssortmentId } from '../../core/services/amountDiscounts.js'
import { useAuthStore } from './auth.js'
import logger from '../../core/services/logger.js'

const log = logger.createScoped('AmountDiscountsStore')

export const useAmountDiscountsStore = defineStore('amountDiscounts', () => {
  // ============================================
  // State
  // ============================================
  const isLoading = ref(false)
  const error = ref(null)
  const assortmentId = ref(null)
  const mapByParitMerakez = ref({})
  const lastLoadedAt = ref(null)

  // ============================================
  // Getters
  // ============================================

  /**
   * Total count of loaded discount items
   */
  const count = computed(() => Object.keys(mapByParitMerakez.value).length)

  /**
   * Check if a paritMerakez has amount discounts
   * @param {string|number} paritMerakez
   * @returns {boolean}
   */
  function hasAmountDiscount(paritMerakez) {
    if (!paritMerakez) return false
    const key = String(paritMerakez)
    const data = mapByParitMerakez.value[key]
    return Array.isArray(data) && data.length > 0
  }

  /**
   * Get discount tiers for a paritMerakez
   * @param {string|number} paritMerakez
   * @returns {Array|null} Array of { minQty, discountPercent } sorted by minQty ASC
   */
  function getDiscountTiers(paritMerakez) {
    if (!paritMerakez) return null
    const key = String(paritMerakez)
    const data = mapByParitMerakez.value[key]
    return Array.isArray(data) && data.length > 0 ? data : null
  }

  /**
   * Get discount data for a paritMerakez (alias for getDiscountTiers)
   * @param {string|number} paritMerakez
   * @returns {Array|null}
   */
  function getDiscountData(paritMerakez) {
    return getDiscountTiers(paritMerakez)
  }

  // ============================================
  // Actions
  // ============================================

  /**
   * Reset store to initial state
   */
  function reset() {
    isLoading.value = false
    error.value = null
    assortmentId.value = null
    mapByParitMerakez.value = {}
    lastLoadedAt.value = null
    log.info('Amount discounts store reset')
  }

  /**
   * Load all discounts for the current user's assortment
   * Uses auth.client.merakez to determine assortment, or override if provided
   * Caches by assortmentId - won't re-fetch if already loaded
   * @param {Object} options - Optional settings
   * @param {string} options.assortmentIdOverride - Override assortment ID (for agent mode)
   * @returns {Promise<{count: number}>}
   */
  async function loadForCurrentUser(options = {}) {
    const authStore = useAuthStore()

    // Use override if provided, otherwise derive from auth user's client
    const targetAssortment = options.assortmentIdOverride
      ? getDiscountAssortmentId({ merakez: options.assortmentIdOverride })
      : getDiscountAssortmentId(authStore.user?.client)

    // Skip if already loaded for this assortment (unless override is forcing reload)
    if (
      assortmentId.value === targetAssortment &&
      count.value > 0 &&
      !options.assortmentIdOverride
    ) {
      log.debug('Amount discounts already loaded for assortment', {
        assortmentId: targetAssortment,
      })
      return { count: count.value }
    }

    // If override is provided and we already have this assortment, still return cached data
    if (
      options.assortmentIdOverride &&
      assortmentId.value === targetAssortment &&
      count.value > 0
    ) {
      log.debug('Amount discounts already loaded for override assortment', {
        assortmentId: targetAssortment,
      })
      return { count: count.value }
    }

    isLoading.value = true
    error.value = null

    try {
      const db = getFirebaseDb()
      const discountsRef = collection(db, 'AmountDiscounts', targetAssortment, 'discounts')

      log.info('Loading amount discounts for assortment', { assortmentId: targetAssortment })

      const snapshot = await getDocs(discountsRef)

      // Build map by paritMerakez (doc.id)
      // Only include if document has valid discounts, and store the normalized data
      const newMap = {}
      snapshot.forEach((doc) => {
        const data = doc.data()
        // Check if document has valid discounts array
        if (!data?.discounts || !Array.isArray(data.discounts)) return

        // Normalize and filter valid discount entries
        const validDiscounts = []
        for (const entry of data.discounts) {
          const minQty = Number(entry.minQty)
          const discount = Number(entry.discount)
          if (!isNaN(minQty) && minQty > 0 && !isNaN(discount) && discount > 0) {
            validDiscounts.push({ minQty, discountPercent: discount })
          }
        }

        // Only store if we have valid discounts
        if (validDiscounts.length > 0) {
          const key = String(doc.id)
          // Sort by minQty ascending
          validDiscounts.sort((a, b) => a.minQty - b.minQty)
          newMap[key] = validDiscounts
        }
      })

      // Update state
      assortmentId.value = targetAssortment
      mapByParitMerakez.value = newMap
      lastLoadedAt.value = new Date()

      log.info('Amount discounts loaded successfully', {
        assortmentId: targetAssortment,
        count: Object.keys(newMap).length,
      })

      return { count: Object.keys(newMap).length }
    } catch (err) {
      log.error('Failed to load amount discounts', { assortmentId: targetAssortment, error: err })
      error.value = err.message || 'Failed to load amount discounts'
      return { count: 0 }
    } finally {
      isLoading.value = false
    }
  }

  return {
    // State
    isLoading,
    error,
    assortmentId,
    mapByParitMerakez,
    lastLoadedAt,

    // Getters
    count,

    // Methods
    hasAmountDiscount,
    getDiscountTiers,
    getDiscountData,

    // Actions
    reset,
    loadForCurrentUser,
  }
})
